import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Input from "../../../../components/Input";
import Dropdown from "../../../../components/Dropdown";
import { TRANSACTION_STATUS, VALIDATION_FIELD_MAPPING } from "../../../../utils/appConstant";
import { ListFilters } from "../../../../interfaces/transaction.interface";
import { getStartEndOfMonth } from "../../../../utils/methods";
import useDebounce from "../../../../hooks/useDebounce";

interface FilterProps {
  status: any[];
  allStatus: any[];
  properties: any[];
  allProperties: any[];
  transactionTypes: any[];
  allTransactionTypes: any[];
  crdrOptions: any[];
  allCrdrOptions: any[];
  managers: any[];
  allManagers: any[];
  allReviewedByOptions: any[];
  reviewedByOptions?: any[];
  vendors: any[];
  allVendors: any[];
  chargeableOptions: any[];
  allChargeableOptions: any[];
  paidByOptions: any[];
  allPaidByOptions: any[];
  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
  initialFilters: ListFilters;
  onApply: (filters: ListFilters) => void;
  onReset: () => void;
}

const Filter: React.FC<FilterProps> = ({
  status, allStatus, properties, allProperties, transactionTypes, allTransactionTypes, crdrOptions, allCrdrOptions, managers, allManagers,
  allReviewedByOptions, reviewedByOptions, vendors, allVendors, chargeableOptions, allChargeableOptions, paidByOptions, allPaidByOptions, dropdownSearchData, onDropdownSearchChange,
  initialFilters, onApply, onReset
}) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<ListFilters>(initialFilters);
  const debouncedSearchText = useDebounce(filters.searchText, 500);

  const defaultDates = useMemo(() => getStartEndOfMonth(), []);
  const defaultStatus = useMemo(() => [TRANSACTION_STATUS.unbilled, TRANSACTION_STATUS.invoiced], []);

  // Calculate initial filtersApplied state
  const isApplied = filters.searchText !== '' ||
    filters.propertyId !== '' ||
    filters.transactionType !== '' ||
    filters.debitCredit !== '' ||
    filters.assignedTo !== '' ||
    filters.reviewedBy !== '' ||
    filters.vendorId !== '' ||
    filters.chargeable !== '' ||
    filters.paidBy !== '' ||
    filters.startDate !== defaultDates.startDate ||
    filters.endDate !== defaultDates.endDate ||
    JSON.stringify(filters.status) !== JSON.stringify(defaultStatus);

  const [filtersApplied, setFiltersApplied] = useState(isApplied);

  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  // Effect to handle Real-time application
  useEffect(() => {
    const filtersToApply = {
      ...filters,
      searchText: debouncedSearchText
    };

    const applied = filtersToApply.searchText !== '' ||
      filtersToApply.propertyId !== '' ||
      filtersToApply.transactionType !== '' ||
      filtersToApply.debitCredit !== '' ||
      filtersToApply.assignedTo !== '' ||
      filtersToApply.reviewedBy !== '' ||
      filtersToApply.vendorId !== '' ||
      !!filtersToApply.chargeable ||
      !!filtersToApply.paidBy ||
      filtersToApply.startDate !== defaultDates.startDate ||
      filtersToApply.endDate !== defaultDates.endDate ||
      JSON.stringify(filtersToApply.status) !== JSON.stringify(defaultStatus);

    setFiltersApplied(applied);

    onApply(filtersToApply);
  }, [debouncedSearchText, filters.propertyId, filters.transactionType, filters.debitCredit, filters.vendorId, filters.assignedTo, filters.reviewedBy, filters.chargeable, filters.paidBy, filters.startDate, filters.endDate, filters.status, defaultDates, defaultStatus, onApply]);

  const handleInputChange = useCallback((event: any, name: string) => {
    setFilters((prev: ListFilters) => ({ ...prev, [name]: event.target.value }));
  }, []);

  const handleReset = () => {
    const defaults: ListFilters = {
      status: [TRANSACTION_STATUS.unbilled, TRANSACTION_STATUS.invoiced],
      searchText: '',
      propertyId: '',
      transactionType: '',
      debitCredit: '',
      assignedTo: '',
      reviewedBy: '',
      vendorId: '',
      chargeable: '',
      paidBy: '',
      ...getStartEndOfMonth(),
    };
    setFilters(defaults);
    onReset();
  };

  return (
    <div className={`card mb-4 ${filtersApplied ? 'border border-primary border-2' : ''}`}>
      <div className="card-body">
        <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
          <h5 className="m-0 text-dark">{t("common.filters")}</h5>
          {filtersApplied && (
            <small
              onClick={handleReset}
              className="cursor-pointer text-primary"
            >
              {t("common.clearFilters")}
            </small>
          )}
        </div>

        <div className="col-12">
          <div className="row g-3">
            <div className="col-md-2 col-sm-4">
              <Input
                type="date"
                id={VALIDATION_FIELD_MAPPING.startDate}
                name={VALIDATION_FIELD_MAPPING.startDate}
                placeholder={t('transactions.list.pageTexts.filters.startDate')}
                value={filters.startDate}
                max={filters.endDate}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Input
                type="date"
                id={VALIDATION_FIELD_MAPPING.endDate}
                name={VALIDATION_FIELD_MAPPING.endDate}
                placeholder={t('transactions.list.pageTexts.filters.endDate')}
                value={filters.endDate}
                min={filters.startDate || undefined}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-4 col-sm-4">
              <Dropdown
                values={filters.propertyId ? [filters.propertyId] : []}
                options={(properties || []).map((property) => ({
                  label: property.name,
                  value: property.id
                }))}
                allOptions={(allProperties || []).map((property) => ({
                  label: property.name,
                  value: property.id
                }))}
                id={VALIDATION_FIELD_MAPPING.propertyId}
                name={VALIDATION_FIELD_MAPPING.propertyId}
                searchText={dropdownSearchData.propertyId}
                onSearch={onDropdownSearchChange}
                placeholder={t('transactions.list.pageTexts.filters.selectProperty')}
                label={t('transactions.list.pageTexts.filters.property')}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-4 col-sm-4">
              <Dropdown
                values={filters.transactionType ? [filters.transactionType] : []}
                options={(transactionTypes || []).map((transactionType) => ({
                  label: transactionType.name,
                  value: transactionType.id,
                }))}
                allOptions={(allTransactionTypes || []).map((transactionType) => ({
                  label: transactionType.name,
                  value: transactionType.id,
                }))}
                id={VALIDATION_FIELD_MAPPING.transactionType}
                name={VALIDATION_FIELD_MAPPING.transactionType}
                searchText={dropdownSearchData.transactionType}
                onSearch={onDropdownSearchChange}
                placeholder={t('transactions.list.pageTexts.filters.selectTransactionType')}
                label={t('transactions.list.pageTexts.filters.transactionType')}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Dropdown
                values={filters.assignedTo && filters.assignedTo.toString().trim() ? [filters.assignedTo] : []}
                options={(managers || []).map((manager: any) => ({
                  label: manager.name,
                  value: manager.id
                }))}
                allOptions={(allManagers || []).map((manager: any) => ({
                  label: manager.name,
                  value: manager.id
                }))}
                id={VALIDATION_FIELD_MAPPING.assignedTo}
                name={VALIDATION_FIELD_MAPPING.assignedTo}
                searchText={dropdownSearchData.assignedTo}
                onSearch={onDropdownSearchChange}
                placeholder={t("transactions.list.pageTexts.filters.selectAssignedTo")}
                label={t("transactions.list.pageTexts.filters.assignedTo")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Dropdown
                values={filters.reviewedBy ? [filters.reviewedBy] : []}
                options={(reviewedByOptions || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                allOptions={(allReviewedByOptions || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                id={VALIDATION_FIELD_MAPPING.reviewedBy}
                name={VALIDATION_FIELD_MAPPING.reviewedBy}
                searchText={dropdownSearchData.reviewedBy}
                placeholder={t('transactions.list.pageTexts.filters.selectReviewedBy')}
                label={t('transactions.list.pageTexts.filters.reviewedBy')}
                onChange={handleInputChange}
                onSearch={onDropdownSearchChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Dropdown
                values={filters.debitCredit ? [filters.debitCredit] : []}
                options={(crdrOptions || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                allOptions={(allCrdrOptions || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                id={VALIDATION_FIELD_MAPPING.debitCredit}
                name={VALIDATION_FIELD_MAPPING.debitCredit}
                searchText={dropdownSearchData.debitCredit}
                onSearch={onDropdownSearchChange}
                placeholder={t('transactions.list.pageTexts.filters.selectDebitCredit')}
                label={t('transactions.list.pageTexts.filters.debitCredit')}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-3 col-sm-4">
              <Dropdown
                multiSelect
                values={filters.status}
                options={(status || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                allOptions={(allStatus || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                id={VALIDATION_FIELD_MAPPING.status}
                name={VALIDATION_FIELD_MAPPING.status}
                searchText={dropdownSearchData.status}
                onSearch={onDropdownSearchChange}
                placeholder={t('transactions.list.pageTexts.filters.selectStatus')}
                label={t('transactions.list.pageTexts.filters.status')}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-3 col-sm-4">
              <Dropdown
                values={filters.vendorId ? [filters.vendorId] : []}
                options={(vendors || []).map((vendor: any) => ({
                  label: vendor.name,
                  value: vendor.id
                }))}
                allOptions={(allVendors || []).map((vendor: any) => ({
                  label: vendor.name,
                  value: vendor.id
                }))}
                id={VALIDATION_FIELD_MAPPING.vendorId}
                name={VALIDATION_FIELD_MAPPING.vendorId}
                searchText={dropdownSearchData.vendorId}
                onSearch={onDropdownSearchChange}
                placeholder={t("transactions.list.pageTexts.filters.selectVendor")}
                label={t("transactions.list.pageTexts.filters.vendor")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-3 col-sm-4">
              <Dropdown
                values={filters.chargeable ? [filters.chargeable] : []}
                options={(chargeableOptions || []).map((item: any) => ({
                  label: item.name,
                  value: item.id,
                }))}
                allOptions={(allChargeableOptions || []).map((item: any) => ({
                  label: item.name,
                  value: item.id,
                }))}
                id="chargeable"
                name="chargeable"
                searchText={dropdownSearchData.chargeable}
                onSearch={onDropdownSearchChange}
                placeholder={t('transactions.list.pageTexts.filters.selectChargeable')}
                label={t('transactions.list.pageTexts.filters.chargeableNonChargeable')}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-3 col-sm-4">
              <Dropdown
                values={filters.paidBy ? [filters.paidBy] : []}
                options={(paidByOptions || []).map((item: any) => ({
                  label: item.name,
                  value: item.id,
                }))}
                allOptions={(allPaidByOptions || []).map((item: any) => ({
                  label: item.name,
                  value: item.id,
                }))}
                id={VALIDATION_FIELD_MAPPING.paidBy}
                name={VALIDATION_FIELD_MAPPING.paidBy}
                searchText={dropdownSearchData.paidBy}
                onSearch={onDropdownSearchChange}
                placeholder={t('transactions.list.pageTexts.filters.selectPaidBy')}
                label={t('transactions.list.pageTexts.filters.paidBy')}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-3 col-sm-12">
              <Input
                type="search"
                id={VALIDATION_FIELD_MAPPING.searchText}
                name={VALIDATION_FIELD_MAPPING.searchText}
                placeholder={t("transactions.list.pageTexts.filters.searchPlaceholder")}
                value={filters.searchText}
                onChange={handleInputChange}
                autoComplete="on"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Filter;